import React, { useState } from 'react';
import { Image } from '../../commonTypes/image';
import colors from '../../helpers/colors';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import { TestimonialImageProps } from './TestimonialImage';
import TestimonialQuote from './TestimonialQuote';
import {
  AnimatedTestimonialImage,
  NavigationButtonsContainer,
  NextArrow,
  NextButton,
  PreviousArrow,
  PreviousButton,
  TestimonialImageAnimationProps,
  TestimonialsContainer,
  TestimonialsOverflow,
} from './styles';

export type TestimonialWithImageItem = {
  image: Image;
  quote: string;
  authorName?: string;
  authorJobDescription?: string;
};

export type TestimonialWithImageProps = {
  testimonials: TestimonialWithImageItem[];
  useStickArrows?: boolean;
  previousButtonScreenReaderText?: string;
  nextButtonScreenReaderText?: string;
} & ComponentProps;

type AnimatedTestimonialImageProps = TestimonialImageProps &
  TestimonialImageAnimationProps;

type TransitionImages = {
  current: AnimatedTestimonialImageProps | null;
  old: AnimatedTestimonialImageProps | null;
};

const TestimonialWithImage: React.FC<TestimonialWithImageProps> = ({
  testimonials,
  useStickArrows = false,
  previousButtonScreenReaderText = 'Previous testimonial',
  nextButtonScreenReaderText = 'Next testimonial',
  ...props
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [oldIndex, setOldIndex] = useState<number | undefined>(undefined);
  const [renderedImages, setRenderedImages] = useState<TransitionImages>({
    current: testimonials?.length
      ? {
          image: testimonials[currentIndex].image,
        }
      : null,
    old: null,
  });

  const getPreviousIndex = (index: number): number => {
    return (index + testimonials.length - 1) % testimonials.length;
  };

  const goToPreviousTestimonial = (): void => {
    setRenderedImages({
      current: {
        ...testimonials[getPreviousIndex(currentIndex)],
        enterFromPrevious: true,
      },
      old: {
        ...testimonials[currentIndex],
        exitToNext: true,
      },
    });
    setOldIndex(currentIndex);
    setCurrentIndex(getPreviousIndex(currentIndex));
  };

  const getNextIndex = (index: number): number => {
    return (index + 1) % testimonials.length;
  };

  const goToNextTestimonial = (): void => {
    setRenderedImages({
      current: {
        ...testimonials[getNextIndex(currentIndex)],
        enterFromNext: true,
      },
      old: {
        ...testimonials[currentIndex],
        exitToPrevious: true,
      },
    });
    setOldIndex(currentIndex);
    setCurrentIndex(getNextIndex(currentIndex));
  };

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <TestimonialsOverflow {...componentProps(props)}>
      <TestimonialsContainer>
        {/* Both current and old set a key here to force each to re-render, otherwise React only replaces the contents inside the component, so doesn't retrigger animation*/}
        {renderedImages.current && (
          <AnimatedTestimonialImage key={currentIndex} {...renderedImages.current} />
        )}
        {/* We render the old image here as we need it to exist while it animates away.*/}
        {renderedImages.old && (
          <AnimatedTestimonialImage key={oldIndex} {...renderedImages.old} />
        )}
        {testimonials.length > 1 && (
          <NavigationButtonsContainer>
            <PreviousButton
              aria-label={previousButtonScreenReaderText}
              onClick={goToPreviousTestimonial}>
              <PreviousArrow
                name={useStickArrows ? 'stick-arrow-up' : 'arrow-up'}
                size="small"
                color={colors.neutral01}
              />
            </PreviousButton>
            <NextButton
              aria-label={nextButtonScreenReaderText}
              onClick={goToNextTestimonial}>
              <NextArrow
                name={useStickArrows ? 'stick-arrow-down' : 'arrow-down'}
                size="small"
                color={colors.neutral01}
              />
            </NextButton>
          </NavigationButtonsContainer>
        )}
        <TestimonialQuote
          content={testimonials[currentIndex]}
          oldContent={oldIndex === undefined ? undefined : testimonials[oldIndex]}
        />
      </TestimonialsContainer>
    </TestimonialsOverflow>
  );
};

export default applyComponentTheme(TestimonialWithImage, 'TestimonialWithImage');
